import type {
  AffiliateLinkInspectionResource,
  AffiliateLinkInspectionResourceInspectionResult,
  AffiliateLinkResource,
  ListAffiliateLinks200,
} from '@metapic-apps/shared'
import { computed, type Ref, ref, watch } from 'vue'

import {
  makeQueryParamsForAffiliateLinks,
  normalizeAffiliateLinkDetail,
  normalizeAffiliateLinks,
  normalizeMatchingAdvertisers,
} from '@/lib/adapters/affiliate-links.adapter'
import { normalizeTrafficSourcesCosts } from '@/lib/adapters/traffic-source-costs.adapter'
import { useApi } from '@/lib/helpers/api.helpers'
import { handleErrorMessage } from '@/lib/helpers/error.helpers'
import { request } from '@/lib/services'
import type { AdvertiserType } from '@/lib/types/advertiser.types'
import type { TrafficSourceCostServerType } from '@/lib/types/traffic-source-costs.types'
import { useErrorStore } from '@/stores/error'

export const useGetAffiliateLinks = (userIdRef: Ref<number>) => {
  const page = ref(1)

  // reset to page 1 when the creator changes
  watch(userIdRef, () => {
    page.value = 1
  })

  const {
    data: response,
    isLoading,
    error,
    fetchData: refetch,
  } = useApi(async () => {
    return request.get<ListAffiliateLinks200>('/v2/affiliate-links', {
      params: makeQueryParamsForAffiliateLinks(userIdRef.value, page.value),
    })
  })

  const data = computed(() =>
    normalizeAffiliateLinks(
      (response.value?.data.data ?? []) as Parameters<typeof normalizeAffiliateLinks>[0],
    ),
  )
  const total = computed(() => response.value?.data.meta.total ?? 0)
  const totalPages = computed(() => response.value?.data.meta.last_page ?? 0)

  return { data, total, totalPages, page, isLoading, error, refetch }
}

export const useGetAffiliateLink = (linkIdRef: Ref<number>) => {
  const {
    data: response,
    isLoading,
    error,
    fetchData: refetch,
  } = useApi(async () => {
    return request.get<AffiliateLinkResource>(`/v2/affiliate-links/${linkIdRef.value}`)
  })

  const data = computed(() =>
    response.value
      ? normalizeAffiliateLinkDetail(
          response.value.data as Parameters<typeof normalizeAffiliateLinkDetail>[0],
        )
      : undefined,
  )

  return { data, isLoading, error, refetch }
}

export const useUpdateAffiliateLink = () => {
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()

  const updateLink = async (linkId: number, link: string): Promise<boolean> => {
    try {
      isLoading.value = true
      error.value = null
      await request.post(`/admin/tags/${linkId}/change-link`, { link })
      return true
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
      return false
    } finally {
      isLoading.value = false
    }
  }

  return { updateLink, isLoading, error }
}

export const useInspectAffiliateLink = (userId: number) => {
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()

  const inspect = async (
    url: string,
  ): Promise<{
    inspectionResult: AffiliateLinkInspectionResourceInspectionResult
    advertisers: AdvertiserType[]
  } | null> => {
    try {
      isLoading.value = true
      error.value = null
      const response = await request.post<AffiliateLinkInspectionResource>(
        `/v2/users/${userId}/inspections/affiliate-link`,
        { url },
      )
      return {
        inspectionResult: response.data.inspection_result,
        advertisers: normalizeMatchingAdvertisers(response.data.matching_advertisers),
      }
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
      return null
    } finally {
      isLoading.value = false
    }
  }

  return { inspect, isLoading, error }
}

export const useConvertAffiliateLink = (userId: number) => {
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()
  const convertedLink = ref<AffiliateLinkResource | null>(null)

  const normalizedCosts = computed(() => {
    const costs = convertedLink.value?.traffic_sources_costs
    if (!costs?.length) {
      return []
    }
    const currency = convertedLink.value?.advertiser?.currency ?? ''
    return normalizeTrafficSourcesCosts(costs as unknown as TrafficSourceCostServerType[], currency)
  })

  const convert = async (
    url: string,
    advertiserId?: number,
  ): Promise<AffiliateLinkResource | null> => {
    try {
      isLoading.value = true
      error.value = null
      const response = await request.post<AffiliateLinkResource>(
        `/v3/users/${userId}/affiliate-links`,
        {
          url,
          advertiser_id: advertiserId,
        },
        {
          headers: {
            'X-Metapic-App-Source': 'admin-app-v2.web',
          },
        },
      )
      convertedLink.value = response.data
      return response.data
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
      return null
    } finally {
      isLoading.value = false
    }
  }

  const clearConvertedLink = () => {
    convertedLink.value = null
  }

  return { convert, convertedLink, normalizedCosts, clearConvertedLink, isLoading, error }
}
